/**
 * Utility functions for converting between frontend schedule formats and backend DisabledDays format
 */

import {
    convert12HourTo24Hour,
    convert24HourTo12Hour,
    TimeRange,
    DaySchedule
} from './weeklyScheduleFormatters';

// Re-export types for convenience
export type { TimeRange, DaySchedule };

export interface SpecialDate {
    id: string;
    date: string;
    isClosedAllDay: boolean;
    timeRanges: TimeRange[];
}

// Re-export the time conversion functions for convenience
export { convert12HourTo24Hour, convert24HourTo12Hour };

/**
 * Format a date string (YYYY-MM-DD) to backend format (MM/DD/YYYY)
 */
export const formatDateForBackend = (dateStr: string): string => {
    if (!dateStr) return '';

    // Parse the date string explicitly to avoid UTC timezone issues
    const [yearStr, monthStr, dayStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);

    return `${month}/${day}/${year}`;
};

/**
 * Parse backend date format (MM/DD/YYYY, M/D/YY, M/D) to frontend format (YYYY-MM-DD)
 */
export const parseDateFromBackend = (backendDate: string): string => {
    if (!backendDate) return '';

    try {
        // Remove any time portion if present
        const datePart = backendDate.split('(')[0].trim();
        const parts = datePart.split('/');

        if (parts.length === 3) {
            let [month, day, year] = parts.map(Number);

            // Validate parsed numbers
            if (isNaN(month) || isNaN(day) || isNaN(year)) {
                return 'none';
            }

            // Handle 2-digit year
            if (year < 100) {
                year += year < 50 ? 2000 : 1900;
            }

            // Validate date ranges
            if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
                return 'none';
            }

            return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        } else if (parts.length === 2) {
            // M/D format - use current year
            const [month, day] = parts.map(Number);

            // Validate parsed numbers
            if (isNaN(month) || isNaN(day)) {
                return 'none';
            }

            // Validate date ranges
            if (month < 1 || month > 12 || day < 1 || day > 31) {
                return 'none';
            }

            const currentYear = new Date().getFullYear();

            return `${currentYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }

        return 'none';
    } catch (error) {
        return 'none';
    }
};

/**
 * Convert SpecialDate to backend DisabledDays format.
 * timeRanges stores the OPEN window entered by the user (e.g. 09:00–17:00).
 * The backend expects CLOSED hours, so we invert: periods outside the open window.
 * Examples:
 * - Closed all day:               "01/02/2026"
 * - Open 09:00–17:00 (partial):   "01/05/2026(00:00-09:00|17:00-23:59)"
 */
export const specialDateToDisabledDay = (specialDate: SpecialDate): string => {
    const dateStr = formatDateForBackend(specialDate.date);

    if (!dateStr) return '';

    if (specialDate.isClosedAllDay || specialDate.timeRanges.length === 0) {
        return dateStr;
    }

    // Convert the single open-hours range to closed periods
    const openRange = specialDate.timeRanges[0];
    const openStart = convert12HourTo24Hour(openRange.startTime);
    const openEnd = convert12HourTo24Hour(openRange.endTime);

    const closedPeriods: string[] = [];
    if (openStart > '00:00') {
        closedPeriods.push(`00:00-${openStart}`);
    }
    if (openEnd < '23:59') {
        closedPeriods.push(`${openEnd}-23:59`);
    }

    if (closedPeriods.length === 0) {
        // Open all day — nothing is actually closed, omit time suffix
        return dateStr;
    }

    return `${dateStr}(${closedPeriods.join('|')})`;
};

/**
 * Convert backend DisabledDays format to SpecialDate.
 * The backend stores CLOSED hours; timeRanges is converted back to the OPEN window.
 * Examples:
 * - "01/02/2026"                        -> isClosedAllDay: true,  timeRanges: []
 * - "01/05/2026(00:00-09:00|17:00-23:59)" -> open 09:00–17:00,   timeRanges: [{ 9:00 AM, 5:00 PM }]
 * - "01/05/2026(00:00-09:00)"           -> open 09:00–23:59,     timeRanges: [{ 9:00 AM, 11:59 PM }]
 * - "01/05/2026(17:00-23:59)"           -> open 00:00–17:00,     timeRanges: [{ 12:00 AM, 5:00 PM }]
 */
export const disabledDayToSpecialDate = (disabledDay: string, holidayDateSet?: Set<string>): SpecialDate | null => {
    if (!disabledDay) return null;

    const hasTime = disabledDay.includes('(');
    const datePart = hasTime ? disabledDay.split('(')[0] : disabledDay;
    const date = parseDateFromBackend(datePart);

    if (date === 'none' || !date) return null;

    const isKnownHoliday = holidayDateSet?.has(date) ?? false;
    const specialDate: SpecialDate = {
        id: isKnownHoliday ? `holiday-${date}` : `custom-${date}`,
        date,
        isClosedAllDay: !hasTime,
        timeRanges: []
    };

    if (hasTime) {
        const timePart = disabledDay.split('(')[1].replace(')', '');
        // Support both pipe and comma delimiters between closed periods
        const closedRanges = timePart.split(/[|,]/).map(r => {
            const [s, e] = r.split('-');
            return { start: s.trim(), end: e.trim() };
        });

        // Derive the open window from the closed periods.
        // Two periods [00:00–A, B–23:59] → open A–B
        // One period  [00:00–A]          → open A–23:59
        // One period  [A–23:59]          → open 00:00–A
        // Anything else → treat as all-day closed
        let openStart: string | null = null;
        let openEnd: string | null = null;

        if (closedRanges.length === 2) {
            const [first, second] = closedRanges;
            if (first.start === '00:00' && second.end === '23:59') {
                openStart = first.end;
                openEnd = second.start;
            }
        } else if (closedRanges.length === 1) {
            const [only] = closedRanges;
            if (only.start === '00:00') {
                openStart = only.end;
                openEnd = '23:59';
            } else if (only.end === '23:59') {
                openStart = '00:00';
                openEnd = only.start;
            }
        }

        if (openStart !== null && openEnd !== null && openEnd > openStart) {
            specialDate.isClosedAllDay = false;
            specialDate.timeRanges = [{
                id: `time_open_${date}`,
                startTime: convert24HourTo12Hour(openStart),
                endTime: convert24HourTo12Hour(openEnd)
            }];
        } else {
            // Cannot derive a clean open window — mark as all-day closed
            specialDate.isClosedAllDay = true;
        }
    }

    return specialDate;
};

/**
 * Convert array of SpecialDates to backend DisabledDays format (comma-separated)
 */
export const specialDatesToDisabledDays = (specialDates: SpecialDate[]): string => {
    return specialDates
        .filter(sd => sd.date) // Only include dates that have a valid date
        .map(specialDateToDisabledDay)
        .filter(Boolean)
        .join(',');
};

/**
 * Convert backend DisabledDays format (comma-separated) to array of SpecialDates
 */
export const disabledDaysToSpecialDates = (disabledDays: string, holidayDateSet?: Set<string>): SpecialDate[] => {
    if (!disabledDays) return [];

    return disabledDays
        .split(',')
        .map(disabledDay => disabledDayToSpecialDate(disabledDay.trim(), holidayDateSet))
        .filter(Boolean) as SpecialDate[];
};

/**
 * Get day of week from date string (YYYY-MM-DD)
 */
export const getDayOfWeek = (dateStr: string): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Parse the date string explicitly to avoid timezone issues
    const [yearStr, monthStr, dayStr] = dateStr.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // Month is 0-indexed
    const day = parseInt(dayStr, 10);

    // Create date in UTC to get the correct day of week
    const date = new Date(Date.UTC(year, month, day));
    return days[date.getUTCDay()];
};

/**
 * Validate backend DisabledDays format
 */
export const isValidDisabledDayFormat = (disabledDay: string): boolean => {
    if (!disabledDay) return false;

    // Check basic format: date or date(time-time|time-time) or date(time-time,time-time)
    const dateTimePattern = /^\d{1,2}\/\d{1,2}(\/\d{2,4})?(\(\d{2}:\d{2}-\d{2}:\d{2}([|,]\d{2}:\d{2}-\d{2}:\d{2})*\))?$/;
    return dateTimePattern.test(disabledDay);
};

/**
 * Validate and clean DisabledDays string
 */
export const cleanDisabledDays = (disabledDays: string): string => {
    if (!disabledDays) return '';

    const validDays = disabledDays
        .split(',')
        .map(d => d.trim())
        .filter(isValidDisabledDayFormat);

    return validDays.join(',');
};
